'use client';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import styles from './mfrm-modal.module.scss';
import { MfrmModalHeader } from './components/mfrm-modal-header';
import { isServer } from '~/utils/host';

interface IModal {
  modalBody: React.ReactNode;
  isOpen: boolean;
  toggleModal(): void;
  headerTitle?: string;
  modalFooter?: React.ReactNode;
}

export const MfrmModal: React.FC<IModal> = ({
  modalBody,
  modalFooter,
  isOpen,
  headerTitle,
  toggleModal,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [closing, setClosing] = useState(false);
  const ANIMATION_DURATION = 500;

  const handleClose = useCallback(() => {
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      toggleModal();
    }, ANIMATION_DURATION);
  }, [toggleModal]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const backdrop = document.querySelector(`.${styles.backdrop}`);
      if (event.button === 0 && event.target === backdrop) {
        handleClose();
      }
    };

    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscKey);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, handleClose]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (isOpen) {
      timeout = setTimeout(() => {
        document.getElementById('modalHeaderTitle')?.focus();
      }, ANIMATION_DURATION);
    }
    return () => clearTimeout(timeout);
  }, [isOpen]);

  if (isServer) return null;

  if (!isOpen && !closing) return null;

  const modalContent = (
    <>
      <div className={styles.modal}>
        <div
          className={`${styles.dialog} ${closing ? styles.bounceOut : styles.bounceIn}`}
          ref={modalRef}
          role="dialog"
          aria-modal="true"
        >
          <div className={styles.content}>
            <MfrmModalHeader
              headerTitle={headerTitle}
              toggleModal={handleClose}
            />
            {modalBody}
            {modalFooter}
          </div>
        </div>
      </div>
      <div className={styles.backdrop} />
    </>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};
